/**
 * Sheet (painel lateral) — segue a ESCALA OFICIAL DE CAMADAS documentada em
 * `src/components/ui/dialog.tsx` (50 -> 80 -> 90 para aninhamentos).
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-17 v1.1.0 - Empilhamento automático: o Sheet passa a participar do
 *            DialogDepthContext, subindo de camada quando aberto de dentro de um Dialog
 *            (corrige o painel de detalhes do contrato ficando atrás do modal de negócio
 *            quando este é aberto pela listagem do dashboard CRM).
 */
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSwipeToClose } from "@/hooks/useSwipeToClose";
import { DialogDepthContext, zClassForDepth } from "@/components/ui/dialog";

/**
 * Raiz do Sheet — incrementa a profundidade de empilhamento, do mesmo modo que o
 * <Dialog>, para que qualquer modal/sheet aninhado fique acima de quem o abriu.
 */
const Sheet = ({ children, ...props }: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>) => {
  const parentDepth = React.useContext(DialogDepthContext);
  return (
    <SheetPrimitive.Root {...props}>
      <DialogDepthContext.Provider value={parentDepth + 1}>{children}</DialogDepthContext.Provider>
    </SheetPrimitive.Root>
  );
};
Sheet.displayName = "Sheet";

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  // Profundidade do PRÓPRIO sheet = valor do provider - 1
  const depth = Math.max(React.useContext(DialogDepthContext) - 1, 0);
  return (
    <SheetPrimitive.Overlay
      className={cn(
        "fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        zClassForDepth(depth),
        className,
      )}
      {...props}
      ref={ref}
    />
  );
});
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  // z-index aplicado dinamicamente em SheetContent (escala 50/80/90)
  "fixed gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /**
   * Habilita o gesto "arrastar para baixo para fechar" no mobile.
   * Padrão: `true`. Aplica-se apenas em viewports mobile e nas variantes
   * `bottom`, `left` e `right` (que costumam ocupar fullscreen no mobile).
   */
  enableSwipeToClose?: boolean;
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ side = "right", className, children, enableSwipeToClose = true, ...props }, ref) => {
    const isMobile = useIsMobile();
    const swipeEnabled = enableSwipeToClose && isMobile;
    // Profundidade do PRÓPRIO sheet = valor do provider - 1
    const depth = Math.max(React.useContext(DialogDepthContext) - 1, 0);

    // Proxy invisível para acionar o fechamento padrão do Radix
    const closeProxyRef = React.useRef<HTMLButtonElement | null>(null);
    const handleSwipeClose = React.useCallback(() => {
      closeProxyRef.current?.click();
    }, []);

    const { contentRef, contentStyle, handlers } = useSwipeToClose({
      enabled: swipeEnabled,
      onClose: handleSwipeClose,
    });

    // Compose ref externo + ref interno
    const composedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref, contentRef],
    );

    return (
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
          ref={composedRef}
          className={cn(
            sheetVariants({ side }),
            zClassForDepth(depth),
            // Deslocamento de arrasto via CSS var — não sobrescreve as
            // animações de slide-in do Radix em estado idle.
            swipeEnabled && "translate-y-[var(--swipe-y,0px)]",
            className,
          )}
          style={swipeEnabled ? contentStyle : undefined}
          {...(swipeEnabled ? handlers : {})}
          {...props}
        >
          {/* Drag handle visual no topo (apenas mobile + swipe habilitado) */}
          {swipeEnabled && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-1.5 z-10 h-1 w-9 -translate-x-1/2 rounded-full bg-muted-foreground/30"
            />
          )}
          {children}
          <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-secondary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
          {/* Close proxy invisível usado pelo swipe-to-close */}
          {swipeEnabled && (
            <SheetPrimitive.Close
              ref={closeProxyRef}
              aria-hidden="true"
              tabIndex={-1}
              className="sr-only"
            />
          )}
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  },
);
SheetContent.displayName = SheetPrimitive.Content.displayName;


const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn("text-lg font-semibold text-foreground", className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
